import { roundQuantity } from '../schedule/unit-of-measure';
import { GEOMETRY_DECIMALS } from './room';

/**
 * O contorno do ambiente — a volta fechada que a planta desenha.
 *
 * É o que substitui "largura × comprimento" como verdade do ambiente. Com os vértices, a
 * área sai por conta fechada **para qualquer forma**: o L, o recorte do shaft, a sala com
 * um canto chanfrado. Não precisa de canto reto, não precisa de par de paredes, não
 * precisa que ninguém adivinhe nada.
 *
 * Os pontos vivem no sistema de coordenadas da **tipologia**, em metros, e não no de cada
 * ambiente. É essa escolha que faz a divisa aparecer sozinha: dois ambientes traçados
 * sobre a mesma planta compartilham a origem, e um trecho de parede em comum é dois
 * segmentos colineares que se sobrepõem — dá para achar por conta, sem ninguém casar
 * parede na mão.
 */
export interface Point {
  readonly x: number;
  readonly y: number;
}

export interface Segment {
  readonly from: Point;
  readonly to: Point;
}

/** Dois pontos a menos de 1 cm são o mesmo ponto — é a precisão de um clique na planta. */
export const POINT_TOLERANCE = 0.01;

export function samePoint(a: Point, b: Point, tolerance = POINT_TOLERANCE): boolean {
  return Math.abs(a.x - b.x) <= tolerance && Math.abs(a.y - b.y) <= tolerance;
}

export function segmentLength(segment: Segment): number {
  return Math.hypot(segment.to.x - segment.from.x, segment.to.y - segment.from.y);
}

/**
 * Os lados do contorno, na ordem, fechando a volta.
 *
 * O último ponto liga no primeiro sem que ninguém precise repeti-lo — repetir seria
 * convidar o contorno com o vértice duplicado, que dá lado de comprimento zero.
 */
export function polygonEdges(outline: readonly Point[]): readonly Segment[] {
  if (outline.length < 3) return [];
  return outline.map((from, indice) => ({
    from,
    to: outline[(indice + 1) % outline.length]!,
  }));
}

/**
 * Área pela fórmula do cadarço, **sem arredondar**.
 *
 * É a que entra em toda conta que multiplica: o volume do pilar 28 × 30 de 3 m é 0,252 m³, e
 * com a área já arredondada a 2 casas (0,08) ele dava 0,27. Arredondar só o resultado.
 *
 * Em módulo de propósito: traçar no sentido horário ou anti-horário é escolha de quem
 * clica, e não pode inverter o sinal da área do quarto.
 */
export function exactPolygonArea(outline: readonly Point[]): number {
  if (outline.length < 3) return 0;

  let dobro = 0;
  for (const { from, to } of polygonEdges(outline)) {
    dobro += from.x * to.y - to.x * from.y;
  }
  return Math.abs(dobro) / 2;
}

/** A área para **mostrar**: a exata, em duas casas. Quem multiplica usa `exactPolygonArea`. */
export function polygonArea(outline: readonly Point[]): number {
  return roundQuantity(exactPolygonArea(outline), GEOMETRY_DECIMALS);
}

export function polygonPerimeter(outline: readonly Point[]): number {
  const total = polygonEdges(outline).reduce((soma, lado) => soma + segmentLength(lado), 0);
  return roundQuantity(total, GEOMETRY_DECIMALS);
}

/** Contorno que fecha e não tem lado degenerado. Menos que isso não é ambiente. */
export function isValidOutline(outline: readonly Point[]): boolean {
  if (outline.length < 3) return false;
  return polygonEdges(outline).every((lado) => segmentLength(lado) > POINT_TOLERANCE);
}

/**
 * O contorno **não se cruza**? Nenhum lado atravessa outro, e nenhum dobra de volta sobre o
 * anterior.
 *
 * O pentagrama e a gravata fecham pontos, lados e até área pela fórmula do cadarço — só que
 * a área do laço é uma soma de voltas que se anulam, e não a de forma nenhuma que alguém
 * tenha desenhado. Encostar um vértice num lado não cruza, e passa: a área dele continua
 * sendo a da região.
 */
export function isSimplePolygon(outline: readonly Point[]): boolean {
  const lados = polygonEdges(outline);
  const n = lados.length;
  let correSobre = false;
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const vizinhos = j === i + 1 || (i === 0 && j === n - 1);
      if (vizinhos) {
        correSobre = correSobre || dobraDeVolta(lados[i]!, lados[j]!);
      } else {
        const resultado = seCruzam(lados[i]!, lados[j]!);
        if (resultado === 'CRUZA') return false;
        correSobre = correSobre || resultado === 'SOBREPOE';
      }
    }
  }
  // Lados que correm um sobre o outro só tornam o contorno inválido quando ele tem área: os
  // pontos todos numa reta não são um contorno que se cruza, são um contorno sem área — e é
  // a conferência da área que diz isso.
  return !(correSobre && exactPolygonArea(outline) > FOLGA_DO_CRUZAMENTO);
}

/** O produto vetorial de `pq` por `pr`: o sinal diz de que lado de `pq` o `r` está. */
function lado(p: Point, q: Point, r: Point): number {
  return (q.x - p.x) * (r.y - p.y) - (q.y - p.y) * (r.x - p.x);
}

const FOLGA_DO_CRUZAMENTO = 1e-9;

/** Dois lados que **não** são vizinhos: cruzam de lado a lado, ou correm juntos sobre a reta. */
function seCruzam(a: Segment, b: Segment): 'CRUZA' | 'SOBREPOE' | 'NADA' {
  const d1 = lado(b.from, b.to, a.from);
  const d2 = lado(b.from, b.to, a.to);
  const d3 = lado(a.from, a.to, b.from);
  const d4 = lado(a.from, a.to, b.to);
  if (d1 * d2 < -FOLGA_DO_CRUZAMENTO && d3 * d4 < -FOLGA_DO_CRUZAMENTO) return 'CRUZA';

  // Todos na mesma reta: só importa se os trechos se sobrepõem de verdade.
  const tamanho = Math.max(segmentLength(a), segmentLength(b), 1);
  const naReta = [d1, d2, d3, d4].every((d) => Math.abs(d) <= FOLGA_DO_CRUZAMENTO * tamanho);
  if (!naReta) return 'NADA';
  const ux = a.to.x - a.from.x;
  const uy = a.to.y - a.from.y;
  const projeta = (p: Point): number => (p.x - a.from.x) * ux + (p.y - a.from.y) * uy;
  const [a0, a1] = [0, projeta(a.to)].sort((p, q) => p - q) as [number, number];
  const [b0, b1] = [projeta(b.from), projeta(b.to)].sort((p, q) => p - q) as [number, number];
  const sobra = Math.min(a1, b1) - Math.max(a0, b0);
  return sobra > POINT_TOLERANCE * segmentLength(a) ? 'SOBREPOE' : 'NADA';
}

/** Dois lados vizinhos que voltam pela mesma reta — o espinho, de área zero. */
function dobraDeVolta(a: Segment, b: Segment): boolean {
  // O vértice em comum é `a.to`/`b.from` (ou o contrário, no par que fecha a volta).
  const ax = a.to.x - a.from.x;
  const ay = a.to.y - a.from.y;
  const bx = b.to.x - b.from.x;
  const by = b.to.y - b.from.y;
  const tamanho = Math.hypot(ax, ay) * Math.hypot(bx, by);
  if (tamanho === 0) return false;
  const colinear = Math.abs(ax * by - ay * bx) <= 1e-9 * tamanho;
  // Vizinhos seguem ao contrário um do outro quando o produto escalar é negativo na ordem
  // `a` depois `b`; no par que fecha a volta (`b` depois `a`) a conta é a mesma.
  return colinear && ax * bx + ay * by < 0;
}

/** Todos os cantos em 90°? Informativo — a conta da área não depende disso. */
export function isRectilinear(outline: readonly Point[], tolerance = POINT_TOLERANCE): boolean {
  return polygonEdges(outline).every((lado) => {
    const dx = Math.abs(lado.to.x - lado.from.x);
    const dy = Math.abs(lado.to.y - lado.from.y);
    return dx <= tolerance || dy <= tolerance;
  });
}

// ── A divisa que aparece sozinha ────────────────────────────────────────────────

/**
 * Quando dois lados são a **mesma parede** vista dos dois ambientes.
 *
 * Nasceu de um caso que a colinearidade pura não resolvia: quem traça cada ambiente pela
 * face interna dele desenha **dois** lados para uma parede só, afastados da espessura do
 * bloco. Quinze centímetros no papel, e a divisa nunca casava — a pessoa via dois quartos
 * encostados na tela e uma lista de paredes livres que dizia o contrário.
 */
export interface Adjacency {
  /**
   * O quanto os dois lados podem estar **afastados**, em metros.
   *
   * Ausente é a folga do ponto, e aí só casam os colineares — o comportamento de quem
   * traça pelo eixo da parede.
   */
  readonly gap?: number;
  /**
   * O quanto a direção deles pode fugir do paralelo, em **graus**.
   *
   * Paralelismo perfeito não existe em traço de mouse: o contorno sai de cliques sobre
   * uma prancha rasterizada, e dois lados da mesma parede fecham 1° ou 2° de diferença
   * com facilidade. Exigir 0° seria exigir que ninguém tremesse a mão.
   */
  readonly angle?: number;
  /** O que ainda conta como ponto, e não como trecho. */
  readonly tolerance?: number;
}

/**
 * O desvio de paralelo que ainda é a mesma parede — cinco graus.
 *
 * É erro de pulso, não chanfro: em quatro metros, cinco graus abrem 35 cm de uma ponta à
 * outra, e quem desenhou um canto de verdade passa **muito** disso.
 */
export const PARALLEL_TOLERANCE_DEGREES = 5;

/**
 * A folga do casamento por geometria: **30 cm**.
 *
 * É a espessura de parede mais grossa que a obra costuma ter, com sobra para o clique. Um
 * corredor, o vão de um shaft ou dois ambientes de verdade separados ficam bem além disso
 * — e continuam sendo duas paredes, como são.
 */
export const WALL_PAIRING_GAP = 0.3;

/** O par recomendado para casar parede traçada: perto o bastante, e quase paralela. */
export const WALL_PAIRING: Adjacency = {
  gap: WALL_PAIRING_GAP,
  angle: PARALLEL_TOLERANCE_DEGREES,
};

/** O trecho que dois lados percorrem juntos, e a que distância um do outro. */
export interface SharedRun {
  /** O comprimento do trecho em comum, medido no eixo de `a`. */
  readonly length: number;
  /**
   * O maior afastamento entre os dois **dentro** desse trecho.
   *
   * Zero é colinear. Acima disso é a espessura que o desenho está mostrando — o que
   * permite à tela dizer "estas duas são uma parede de 15" em vez de só casá-las.
   */
  readonly gap: number;
}

/**
 * O quanto dois lados dividem de parede.
 *
 * Dois lados dividem parede quando correm **quase na mesma direção**, ficam **perto** um
 * do outro e se **sobrepõem**: encostar num ponto só é canto, não é divisa, e por isso o
 * toque de vértice não devolve nada.
 *
 * O afastamento é medido onde eles andam juntos, e não nas pontas soltas: dois lados de
 * tamanhos diferentes se afastam depois que um acaba, e esse pedaço não diz nada sobre o
 * trecho em que os dois existem.
 *
 * É daqui que sai o casamento automático das paredes. O que a pessoa fazia escolhendo num
 * `select`, vinte vezes por tipologia, passa a ser consequência de ter traçado os dois
 * ambientes sobre a mesma planta.
 */
export function sharedRun(
  a: Segment,
  b: Segment,
  adjacency: Adjacency = {},
): SharedRun | undefined {
  const tolerance = adjacency.tolerance ?? POINT_TOLERANCE;
  const afastamentoMaximo = Math.max(adjacency.gap ?? tolerance, tolerance);
  const anguloMaximo = adjacency.angle ?? PARALLEL_TOLERANCE_DEGREES;

  const ax = a.to.x - a.from.x;
  const ay = a.to.y - a.from.y;
  const comprimentoA = Math.hypot(ax, ay);
  const bx = b.to.x - b.from.x;
  const by = b.to.y - b.from.y;
  const comprimentoB = Math.hypot(bx, by);
  if (comprimentoA <= tolerance || comprimentoB <= tolerance) return undefined;

  const ux = ax / comprimentoA;
  const uy = ay / comprimentoA;

  // O **sentido** não entra na conta: o vizinho traça o contorno dele para o outro lado, e
  // a mesma parede aparece de cabeça para baixo. Daí o módulo do cosseno.
  const cosseno = Math.min(1, Math.abs((bx * ux + by * uy) / comprimentoB));
  if ((Math.acos(cosseno) * 180) / Math.PI > anguloMaximo) return undefined;

  const projeta = (ponto: Point): number => (ponto.x - a.from.x) * ux + (ponto.y - a.from.y) * uy;
  // Afastamento **com sinal**: de que lado da reta de `a` o ponto caiu. O sinal é o que
  // permite interpolar entre as duas pontas em vez de perder a inclinação no módulo.
  const afasta = (ponto: Point): number => (ponto.x - a.from.x) * uy - (ponto.y - a.from.y) * ux;

  const inicioB = projeta(b.from);
  const fimB = projeta(b.to);
  const inicio = Math.max(0, Math.min(inicioB, fimB));
  const fim = Math.min(comprimentoA, Math.max(inicioB, fimB));
  const sobreposicao = fim - inicio;
  if (sobreposicao <= tolerance) return undefined;

  const naPosicao = (s: number): number => {
    const dInicio = afasta(b.from);
    const dFim = afasta(b.to);
    if (Math.abs(fimB - inicioB) <= tolerance) return dInicio;
    return dInicio + ((s - inicioB) * (dFim - dInicio)) / (fimB - inicioB);
  };
  const gap = Math.max(Math.abs(naPosicao(inicio)), Math.abs(naPosicao(fim)));
  if (gap > afastamentoMaximo) return undefined;

  return {
    length: roundQuantity(sobreposicao, GEOMETRY_DECIMALS),
    gap: roundQuantity(gap, GEOMETRY_DECIMALS),
  };
}

/** O trecho em comum entre dois lados **colineares** — zero quando não há. */
export function sharedLength(a: Segment, b: Segment, tolerance = POINT_TOLERANCE): number {
  return sharedRun(a, b, { gap: tolerance, tolerance })?.length ?? 0;
}

export interface SharedEdge {
  readonly aIndex: number;
  readonly bIndex: number;
  readonly length: number;
  /** O afastamento entre os dois lados — a espessura que o desenho está mostrando. */
  readonly gap: number;
}

/**
 * Onde dois ambientes se encostam — o par de lados, quanto de parede dividem e a que
 * distância.
 *
 * A tela usa isto para propor o casamento já feito, em vez de pedir que alguém encontre a
 * mesma parede duas vezes numa lista. Vem **ordenado do mais colado para o mais solto**:
 * quando um lado alcança dois candidatos — o shaft estreito colado no quarto é o caso —,
 * quem casa é o que está mais perto, e não o que apareceu primeiro na volta do polígono.
 */
export function sharedEdges(
  a: readonly Point[],
  b: readonly Point[],
  adjacency: Adjacency = {},
): readonly SharedEdge[] {
  const ladosA = polygonEdges(a);
  const ladosB = polygonEdges(b);

  const encontrados: SharedEdge[] = [];
  ladosA.forEach((ladoA, aIndex) => {
    ladosB.forEach((ladoB, bIndex) => {
      const trecho = sharedRun(ladoA, ladoB, adjacency);
      if (trecho !== undefined) {
        encontrados.push({ aIndex, bIndex, length: trecho.length, gap: trecho.gap });
      }
    });
  });
  return [...encontrados].sort((um, outro) => um.gap - outro.gap);
}

// ── Volume ──────────────────────────────────────────────────────────────────────

/**
 * O m³ que sai de uma área e uma espessura.
 *
 * Serve para as duas contas da obra, que são a mesma conta: a alvenaria é área de parede ×
 * espessura do bloco; o reboco é área de face × espessura da massa. A espessura é do
 * **serviço**, não da parede — a mesma parede leva chapisco de 5 mm, emboço de 2 cm e
 * gesso de 5 mm, e são três volumes diferentes do mesmo metro quadrado.
 *
 * Três casas decimais porque é assim que se compra concreto e argamassa.
 */
export function volumeFrom(area: number, thickness: number): number {
  if (!Number.isFinite(area) || !Number.isFinite(thickness)) return 0;
  if (area <= 0 || thickness <= 0) return 0;
  return roundQuantity(area * thickness, 3);
}
